import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type DirectoryUser, type WorkspaceDetail } from '../api';
import { useData } from '../useData';
import { ErrorMsg, Modal } from '../components/Modal';
import { WorkspaceForm } from '../components/WorkspaceForm';

export function WorkspacePage({ onChanged }: { onChanged: () => void }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: ws, error, reload } = useData<WorkspaceDetail>(`/workspaces/${id}`);
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (error) return <p className="error">{error}</p>;
  if (!ws) return <p className="muted">Se încarcă…</p>;

  const canSettings = ws.myPermissions.includes('ws.settings.manage');
  const canMembers = ws.myPermissions.includes('ws.members.manage');
  const canApps = ws.myPermissions.includes('ws.apps.manage');
  const pinned = ws.apps.filter((a) => a.isPinned);
  const others = ws.apps.filter((a) => !a.isPinned);

  async function run(fn: () => Promise<unknown>): Promise<boolean> {
    setActionError(null);
    try { await fn(); reload(); onChanged(); return true; } catch (e) { setActionError((e as Error).message); return false; }
  }

  return (
    <>
      <div className="page-head">
        <h1><span className="dot big" style={{ background: ws.color ?? '#083050' }} />{ws.name}</h1>
        <div className="actions">
          {canSettings && <button onClick={() => setEditing(true)}>Setări</button>}
          {canSettings && (
            <button className="danger" onClick={() => {
              if (confirm(`Arhivați workspace-ul „${ws.name}”? Datele se păstrează.`)) {
                run(() => api.del(`/workspaces/${ws.id}`)).then((ok) => ok && nav('/'));
              }
            }}>Arhivează</button>
          )}
        </div>
      </div>
      {ws.description && <p className="muted">{ws.description}</p>}
      <ErrorMsg error={actionError} />

      {pinned.length > 0 && (
        <section className="pinned-zone">
          <div className="pinned-grid">
            {pinned.map((a) => (
              <div key={a.id} className={`pinned-tile ${a.slug === 'clienti' ? 'clickable' : ''}`} style={{ borderTopColor: ws.color ?? '#00a651' }}
                onClick={() => a.slug === 'clienti' && nav(`/a/${a.id}`)}
                title={a.slug === 'clienti' ? undefined : 'Aplicația urmează să fie construită'}>
                <span className="pinned-icon" style={{ background: ws.color ?? '#00a651' }}>{a.name.slice(0, 1)}</span>
                <div>
                  <h3>{a.name}</h3>
                  <p className="muted small">{a.itemCount} înregistrări</p>
                </div>
                {canApps && (
                  <button className="link pin-btn" title="Desprinde din zona fixată"
                    onClick={(e) => { e.stopPropagation(); run(() => api.put(`/workspaces/${ws.id}/apps/${a.id}/pin`, { pinned: false })); }}>✕</button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2>{pinned.length > 0 ? 'Alte aplicații' : 'Aplicații'}</h2>
        {others.length === 0 && (
          <p className="muted">
            {ws.apps.length === 0 ? 'Nicio aplicație încă. Constructorul de aplicații urmează.' : 'Toate aplicațiile sunt fixate sus.'}
          </p>
        )}
        <div className="cards">
          {others.map((a) => (
            <div key={a.id} className="card">
              <h3>{a.name}</h3>
              <p className="muted">{a.itemCount} înregistrări</p>
              {canApps && (
                <button className="link" onClick={() => run(() => api.put(`/workspaces/${ws.id}/apps/${a.id}/pin`, { pinned: true }))}>
                  Fixează sus
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="section-head">
          <h2>Membri ({ws.members.length})</h2>
          {canMembers && <button onClick={() => setAddingMember(true)}>+ Adaugă membru</button>}
        </div>
        <table>
          <thead><tr><th>Nume</th><th>E-mail</th><th>Rol în workspace</th>{canMembers && <th />}</tr></thead>
          <tbody>
            {ws.members.map((m) => (
              <tr key={m.userId}>
                <td>{m.displayName}</td>
                <td className="muted">{m.email}</td>
                <td>
                  {canMembers ? (
                    <RoleSelect value={m.roleId} onChange={(roleId) =>
                      run(() => api.put(`/workspaces/${ws.id}/members`, { userId: m.userId, roleId }))} />
                  ) : m.roleName}
                </td>
                {canMembers && (
                  <td className="right">
                    <button className="link danger" onClick={() => {
                      if (confirm(`Eliminați pe ${m.displayName} din workspace?`)) {
                        run(() => api.del(`/workspaces/${ws.id}/members/${m.userId}`));
                      }
                    }}>Elimină</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editing && (
        <WorkspaceForm initial={ws} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reload(); onChanged(); }} />
      )}
      {addingMember && (
        <AddMember existing={ws.members.map((m) => m.userId)} onClose={() => setAddingMember(false)}
          onAdd={(userId, roleId) => run(() => api.put(`/workspaces/${ws.id}/members`, { userId, roleId }))
            .then((ok) => ok && setAddingMember(false))} />
      )}
    </>
  );
}

function RoleSelect({ value, onChange }: { value: number; onChange: (roleId: number) => void }) {
  const { data: roles } = useData<{ id: number; name: string }[]>('/directory/workspace-roles');
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {roles?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
    </select>
  );
}

function AddMember(props: { existing: number[]; onClose: () => void; onAdd: (userId: number, roleId: number) => void }) {
  const { data: users } = useData<DirectoryUser[]>('/directory/users');
  const { data: roles } = useData<{ id: number; code: string; name: string }[]>('/directory/workspace-roles');
  const [userId, setUserId] = useState(0);
  const [roleId, setRoleId] = useState(0);
  const candidates = (users ?? []).filter((u) => !props.existing.includes(u.id));
  const memberRole = roles?.find((r) => r.code === 'ws.member')?.id ?? 0;
  return (
    <Modal title="Adaugă membru" onClose={props.onClose}>
      <label>Utilizator
        <select value={userId} onChange={(e) => setUserId(Number(e.target.value))}>
          <option value={0}>— alegeți —</option>
          {candidates.map((u) => <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>)}
        </select>
      </label>
      <label>Rol
        <select value={roleId || memberRole} onChange={(e) => setRoleId(Number(e.target.value))}>
          {roles?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <div className="actions">
        <button onClick={props.onClose}>Renunță</button>
        <button className="primary" disabled={!userId} onClick={() => props.onAdd(userId, roleId || memberRole)}>Adaugă</button>
      </div>
    </Modal>
  );
}
